import { useDialog } from '@/core/dialogsystem/useDialog';
import { DialogQueuePriority } from '@/core/dialogsystem/dialogQueue';
import RatingDialog from '@/core/rating/RatingDialog.vue';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { shouldSeeRatingPrompt } from '@/api/resources/userFeedback.api';
import { userFeedbackKeys } from './userFeedback.keys';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { computed } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useUserFeedback } from './useUserFeedback';
import { UserFeedbackCategory } from '@/api/generated/api.enum';
import { useDeviceInfo } from '../useDeviceInfo';
import { Components } from '@/api/generated/api';
import { now } from 'common/utils/date.utils';

interface IUserFeedbackDialogComposable {
  checkAndShowRatingDialog: (entity?: Components.Schemas.UserFeedbackEntity) => Promise<boolean>;
  invalidateShouldSeePrompt: () => Promise<void>;
  showRatingDialogManual: () => Promise<void>;
  handleDialogHide: (shouldUpdateProfile: boolean) => Promise<void>;
  persistFeedback: (
    feedbackId: number | null,
    payload: {
      rating: number;
      message: string | null;
      trigger: string;
      entity?: Components.Schemas.UserFeedbackEntity;
      entityCount?: number;
    },
    toastMessage?: string,
  ) => Promise<number>;
}

export function useUserRatingDialog(): IUserFeedbackDialogComposable {
  const { whoAmI } = useWhoAmI();
  const queryClient = useQueryClient();
  const { createUserFeedback, updateUserFeedback } = useUserFeedback();
  const { updateProfile } = useWhoAmI();
  const { deviceInfo } = useDeviceInfo();

  const ratingDialog = useDialog({
    component: RatingDialog,
    props: {},
  });

  const userId = computed(() => whoAmI.value?.id);
  const queryKey = computed(() => (userId.value ? userFeedbackKeys.shouldUserSeePrompt(userId.value) : []));

  const { data: shouldShowData } = useQueryWithToast({
    queryKey,
    queryFn: async () => {
      if (!userId.value) return { success: false, showPrompt: false };
      return shouldSeeRatingPrompt({ id: userId.value });
    },
    enabled: computed(() => !!userId.value),
    meta: {
      showErrorToast: false,
    },
  });

  const setRatingDialogSeenAt = async (): Promise<void> => {
    await updateProfile({ ratingDialogSeenAt: now().toISOString() }, false);
  };

  const showAutomaticRatingDialog = (params: {
    entityCountValue?: number;
    entityValue?: Components.Schemas.UserFeedbackEntity;
    triggerValue: string;
  }): void => {
    void ratingDialog.showQueued(
      {
        dedupeKey: params.entityValue ? `rating:${params.entityValue}` : 'rating:automatic',
        priority: DialogQueuePriority.Marketing,
      },
      params,
    );
  };

  const checkAndShowRatingDialog = async (entity?: Components.Schemas.UserFeedbackEntity): Promise<boolean> => {
    await invalidateShouldSeePrompt();

    if (!shouldShowData.value?.showPrompt) {
      return false;
    }

    if (!entity) {
      await setRatingDialogSeenAt();
      showAutomaticRatingDialog({
        triggerValue: 'automatic',
      });
      return true;
    }

    if (!shouldShowData.value.eligibleEntities) {
      return false;
    }

    const eligibleEntity = shouldShowData.value.eligibleEntities.find((e) => e.entity === entity);
    if (eligibleEntity) {
      const entityCount = eligibleEntity.entityCount;
      await setRatingDialogSeenAt();
      showAutomaticRatingDialog({
        triggerValue: 'automatic',
        entityValue: entity,
        entityCountValue: entityCount,
      });
      return true;
    }

    return false;
  };

  const persistFeedback = async (
    feedbackId: number | null,
    payload: {
      rating: number;
      message: string | null;
      trigger: string;
      entity?: Components.Schemas.UserFeedbackEntity;
      entityCount?: number;
    },
    toastMessage?: string,
  ): Promise<number> => {
    const payloadToSend = {
      rating: payload.rating,
      message: payload.message ?? undefined,
      category: UserFeedbackCategory.starRating,
      appVersion: __APP_VERSION__,
      userAgent: deviceInfo.value ?? '',
      source: `in-app-trigger-${payload.trigger}`,
      entity: payload.entity,
      entityCount: payload.entityCount,
    };

    if (feedbackId) {
      await updateUserFeedback(feedbackId, payloadToSend, toastMessage);
      return feedbackId;
    }

    const created = await createUserFeedback(payloadToSend, toastMessage);
    return created.id;
  };

  const showRatingDialogManual = async (): Promise<void> => {
    ratingDialog.show({ triggerValue: 'manual' });
  };

  const invalidateShouldSeePrompt = async (): Promise<void> => {
    if (userId.value) {
      await queryClient.invalidateQueries({ queryKey: userFeedbackKeys.shouldUserSeePrompt(userId.value) });
    }
  };

  // If user did not create a rating, we may still update profile to not show the dialog again
  const handleDialogHide = async (shouldUpdateProfile: boolean): Promise<void> => {
    if (shouldUpdateProfile) {
      await updateProfile({ ratingDialogSeenAt: now().toISOString() }, false);
    }
  };

  return {
    checkAndShowRatingDialog,
    invalidateShouldSeePrompt,
    showRatingDialogManual,
    handleDialogHide,
    persistFeedback,
  };
}
